// Proaktive KI-Hilfe (Dominik 16.10.2026: „die KI-Hilfe soll eine proaktive Chat-Box sein“ – Tipps und Hilfe bei Problemen):
// je Seite einmal je Sitzung ein kurzer Tipp, und sofort ein Angebot, wenn eine Aktion gesperrt ist oder fehlschlägt. Nur im
// Browser gespeichert; abschaltbar („Hinweise aus“). An die KI geht nur das Thema des Fehlers, nie die Meldung selbst.

import type { ApiProblem } from '../api/client';

export interface Tip {
  text: string;
  question: string;
}

const tips: Record<string, Tip> = {
  heute: { text: 'Oben steht, was als Nächstes dran ist – ein Klick führt direkt hin.', question: 'Was sehe ich auf der Startseite?' },
  akquise: {
    text: 'Hat ein Lead schon einen Termin, steht oben „Termin bestätigen“ – dort rufen Sie ohne Akquise-Sperren an.',
    question: 'Wie bestätige ich einen Termin telefonisch?',
  },
  'akquise/wiedervorlagen': { text: 'Überfällige Rückrufe stehen oben. Vereinbarte Rückrufe kommen zur vereinbarten Zeit.', question: 'Wie erledige ich eine Wiedervorlage?' },
  'termine/bestaetigungen': {
    text: 'Fällig ist eine Bestätigung am Werktag vor dem Termin ab 16 Uhr. Nach dem Anruf das Ergebnis eintragen.',
    question: 'Was trage ich bei „Nicht erreicht“ ein?',
  },
  'termine/kalender': { text: 'Mit dem Makler-Filter sehen Sie nur die Termine einer Person.', question: 'Wie filtere ich den Kalender nach Makler?' },
  'termine/detail': { text: 'Alles zum Termin auf einer Seite: Ort mit Navigation, Ansprechpartner und Terminvorbereitung.', question: 'Was steht in der Terminvorbereitung?' },
  'leads/liste': { text: 'Suchen Sie nach Name, Telefonnummer oder Postleitzahl – die Filter bleiben in der Adresszeile erhalten.', question: 'Welche Filter gibt es?' },
  'leads/detail': { text: 'Kontaktdaten ändern Sie über „Daten bearbeiten“, Notizen schreiben Sie unten bei „Notizen“.', question: 'Was sehe ich auf der Lead-Seite?' },
  tickets: { text: 'Eine Rückfrage schließen Sie mit Ergebnis – wer gefragt hat, bekommt Bescheid.', question: 'Wie bearbeite ich eine Rückfrage?' },
  profil: { text: 'Hier stellen Sie ein, welche Benachrichtigungen per E-Mail kommen.', question: 'Wie stelle ich Benachrichtigungen ein?' },
};

export function tipFor(key: string): Tip | undefined {
  return tips[key];
}

const offKey = 'em.ki.proaktiv';
const seenKey = 'em.ki.tipps';
const voiceKey = 'em.ki.stimme';

// Hinweis „mit mir sprechen“ nur einmal je Browser.
export function voiceIntroSeen(): boolean {
  try {
    return localStorage.getItem(voiceKey) === 'gezeigt';
  } catch {
    return true;
  }
}

export function markVoiceIntroSeen() {
  try {
    localStorage.setItem(voiceKey, 'gezeigt');
  } catch {
    // ohne Speicher: kommt nach dem Neuladen wieder
  }
}

export function hintsEnabled(): boolean {
  try {
    return localStorage.getItem(offKey) !== 'aus';
  } catch {
    return true;
  }
}

export function setHintsEnabled(on: boolean) {
  try {
    if (on) localStorage.removeItem(offKey);
    else localStorage.setItem(offKey, 'aus');
  } catch {
    // ohne Speicher: gilt nur bis zum Neuladen
  }
}

function seen(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem(seenKey) ?? '[]') as string[];
  } catch {
    return [];
  }
}

export function tipSeen(key: string): boolean {
  return seen().includes(key);
}

export function markTipSeen(key: string) {
  try {
    sessionStorage.setItem(seenKey, JSON.stringify([...seen(), key]));
  } catch {
    // ohne Speicher: Tipp kann nach dem Neuladen wiederkommen
  }
}

// Themen zu Fehlercodes der API (Aktionen, keine Abfragen); 403 und andere Konflikte allgemein.
const topics: Record<string, string> = {
  contact_policy_blocked: 'ein Anruf durch eine Kontaktregel gesperrt ist (z. B. Mindestabstand, bestehender Termin, Kontaktstopp oder Anrufzeiten)',
  number_suppressed: 'eine Telefonnummer auf der Sperrliste steht',
  number_stopped_elsewhere: 'eine Telefonnummer bei einem anderen Lead für Anrufe gesperrt ist',
  number_erased: 'eine Telefonnummer gelöscht wurde und nicht mehr angerufen werden darf',
  lead_locked: 'ein Lead gerade von jemand anderem bearbeitet wird',
  agent_busy: 'noch ein anderes Gespräch läuft',
  call_not_connected: 'ein Anruf nicht verbunden ist',
  appointment_conflict: 'ein Termin mit einem anderen Termin des Maklers kollidiert',
  appointment_not_upcoming: 'ein Termin nicht mehr ansteht und deshalb nicht mehr bestätigt werden muss',
  confirmation_call: 'nach einem Bestätigungsanruf kein Akquise-Ergebnis eingetragen werden kann',
  out_of_area: 'ein Termin außerhalb des Gebiets des Maklers liegt',
  no_field_sales: 'für einen Termin kein Makler zuständig ist',
  field_sales_missing: 'für einen Termin kein Makler zuständig ist',
  consent_required: 'für die Aufzeichnung die Einwilligung fehlt',
  telephony_disabled: 'die Telefonie nicht eingerichtet ist',
  follow_up_open: 'schon eine offene Wiedervorlage besteht',
  handback_open: 'zu einem Termin schon eine offene Rückgabe besteht',
};

export function problemTopic(p: ApiProblem): string | undefined {
  if (topics[p.code]) return topics[p.code];
  if (p.status === 403) return 'mir für eine Aktion die Berechtigung fehlt';
  if (p.status === 409) return 'eine Aktion wegen eines Konflikts nicht geklappt hat';
  return undefined;
}

export function problemQuestion(topic: string): string {
  return `Ich bekomme eine Meldung, weil ${topic}. Woran liegt das und was kann ich tun?`;
}
